import { DeleteDefaultEnvironmentReleasePlan } from '@gonfalon/openapi/operations/DeleteDefaultEnvironmentReleasePlan';
import { DeleteEnvironmentReleasePlan } from '@gonfalon/openapi/operations/DeleteEnvironmentReleasePlan';
import { GetDefaultEnvironmentReleasePlan } from '@gonfalon/openapi/operations/GetDefaultEnvironmentReleasePlan';
import { GetEnvironmentReleasePlan } from '@gonfalon/openapi/operations/GetEnvironmentReleasePlan';
import { GetEnvironmentReleasePlans } from '@gonfalon/openapi/operations/GetEnvironmentReleasePlans';
import { GetProjectReleasePlans } from '@gonfalon/openapi/operations/GetProjectReleasePlans';
import { PostEnvironmentReleasePlan } from '@gonfalon/openapi/operations/PostEnvironmentReleasePlan';
import { PutDefaultEnvironmentReleasePlan } from '@gonfalon/openapi/operations/PutDefaultEnvironmentReleasePlan';
import { PutEnvironmentReleasePlan } from '@gonfalon/openapi/operations/PutEnvironmentReleasePlan';
import { getQueryClient } from '@gonfalon/react-query-client/getQueryClient';

import { createMutationHook } from '../internal/createMutationHook';
import { createQueryOptions } from '../internal/createQueryOptions';

// fetch a list of environment release plans
export const getEnvironmentReleasePlansQuery = createQueryOptions(GetEnvironmentReleasePlans);

// fetch a single environment release plan by key
export const getEnvironmentReleasePlanQuery = createQueryOptions(GetEnvironmentReleasePlan);

export const getProjectReleasePlansQuery = createQueryOptions(GetProjectReleasePlans);

export const getDefaultEnvironmentReleasePlanQuery = createQueryOptions(GetDefaultEnvironmentReleasePlan);

async function invalidateReleasePlanLists(projectKey: string) {
  const queryClient = getQueryClient();
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: getEnvironmentReleasePlansQuery.partialQueryKey({ projectKey }) }),
    queryClient.invalidateQueries({ queryKey: getProjectReleasePlansQuery.partialQueryKey({ projectKey }) }),
    queryClient.invalidateQueries({ queryKey: getDefaultEnvironmentReleasePlanQuery.partialQueryKey({ projectKey }) }),
  ]).catch(() => {});
}

async function invalidateReleasePlanListsAndDetails(projectKey: string) {
  await Promise.all([
    invalidateReleasePlanLists(projectKey),
    getQueryClient()
      .invalidateQueries({ queryKey: getEnvironmentReleasePlanQuery.partialQueryKey({ projectKey }) })
      .catch(() => {}),
  ]);
}

export const useCreateEnvironmentReleasePlan = createMutationHook(PostEnvironmentReleasePlan, {
  onSuccess: async (_, { projectKey }) => invalidateReleasePlanLists(projectKey),
});

export const useDeleteEnvironmentReleasePlan = createMutationHook(DeleteEnvironmentReleasePlan, {
  onSuccess: async (_, { projectKey, environmentKey, releasePlanKey }) => {
    getQueryClient().removeQueries({
      queryKey: getEnvironmentReleasePlanQuery.partialQueryKey({ projectKey, environmentKey, releasePlanKey }),
    });
    await invalidateReleasePlanLists(projectKey);
  },
});

export const useUpdateEnvironmentReleasePlan = createMutationHook(PutEnvironmentReleasePlan, {
  onSuccess: async (_, { projectKey }) => invalidateReleasePlanListsAndDetails(projectKey),
});

export const useSetDefaultEnvironmentReleasePlan = createMutationHook(PutDefaultEnvironmentReleasePlan, {
  onSuccess: async (_, { projectKey }) => invalidateReleasePlanListsAndDetails(projectKey),
});

export const useClearDefaultEnvironmentReleasePlan = createMutationHook(DeleteDefaultEnvironmentReleasePlan, {
  onSuccess: async (_, { projectKey }) => invalidateReleasePlanListsAndDetails(projectKey),
});
